import React, { useState, useEffect, useRef } from 'react';
import RouteSeo from '../seo/RouteSeo';
import { useNavigate } from 'react-router-dom';
import { Clock, ListChecks, PenLine, Share2, Globe2, Layers, Lightbulb, Sparkles } from 'lucide-react';
import { apiPublicGet } from '../api';
import { parseMarkdown } from '../utils/parseMarkdown';
import { BlockItem, ArticleItem } from '../types/helpArticles';
import MediaPlaceholder from '../components/MediaPlaceholder';
import HelpVideo from '../components/HelpVideo';
import ZoomableImage from '../components/ZoomableImage';
import '../App.css';

interface StepGroup {
  stepN: number;
  stepId: string;
  title: string;
  blocks: BlockItem[];
}
interface StageGroup {
  stageN: number;
  t: string;
  steps: StepGroup[];
}

function groupBlocks(blocks: BlockItem[]): StageGroup[] {
  const stages: StageGroup[] = [];
  let currentStage: StageGroup | null = null;
  let currentStep: StepGroup | null = null;
  let stageN = 0;
  let stepN = 0;
  for (const block of blocks) {
    if (block.k === 'stage') {
      stageN++;
      currentStep = null;
      currentStage = { stageN, t: block.t, steps: [] };
      stages.push(currentStage);
    } else if (block.k === 'step') {
      stepN++;
      currentStep = { stepN, stepId: `step-${stepN}`, title: block.title, blocks: [] };
      currentStage?.steps.push(currentStep);
    } else if (currentStep) {
      currentStep.blocks.push(block);
    }
  }
  return stages;
}

// stepTitle names a video's card after its step ("Map the relationships"), which is
// what the video itself opens on; the blocks' own tag just says "Walkthrough".
interface MediaProps { kind?: 'video' | 'shot'; tag?: string; duration?: string; label?: string; caption?: string; src?: string; stepTitle?: string; }
const Media: React.FC<MediaProps> = ({ kind = 'video', tag, duration, label, caption, src, stepTitle }) => {
  const videoTitle = label || stepTitle || tag;
  return (
    <figure className="tut-step-media">
      {kind === 'video' ? (
        src ? (
          <HelpVideo src={src} eyebrow="Tutorial" title={videoTitle} duration={duration} />
        ) : (
          <MediaPlaceholder eyebrow="Tutorial" title={videoTitle} duration={duration} />
        )
      ) : src ? (
        <ZoomableImage src={src} alt={label || tag || 'Screenshot'} />
      ) : (
        <MediaPlaceholder
          kind="shot"
          eyebrow="Tutorial"
          title={label && label !== 'Screenshot' ? label : tag}
        />
      )}
      {caption && <figcaption className="tut-media-cap">{parseMarkdown(caption)}</figcaption>}
    </figure>
  );
};

interface CalloutProps { kind: 'tip' | 'extra'; title: string; c: string; }
const Callout: React.FC<CalloutProps> = ({ kind, title, c }) => (
  <div className={`tut-callout tut-callout--${kind}`}>
    <span className="ca-icon">
      {kind === 'tip' ? <Lightbulb size={18} /> : <Sparkles size={18} />}
    </span>
    <div className="ca-body">
      <strong>{title}</strong>
      {parseMarkdown(c)}
    </div>
  </div>
);

function renderContentBlock(block: BlockItem, key: number, stepTitle?: string): React.ReactElement | null {
  switch (block.k) {
    case 'p':
      return <p key={key}>{parseMarkdown(block.c)}</p>;
    case 'steps':
      return <ol key={key} className="tut-substeps">{block.items.map((item, i) => <li key={i}>{parseMarkdown(item)}</li>)}</ol>;
    case 'bullets':
      return <ul key={key} className="tut-substeps">{block.items.map((item, i) => <li key={i}>{parseMarkdown(item)}</li>)}</ul>;
    case 'chips':
      return <div key={key} className="tut-chips">{block.items.map((chip, i) => <span key={i} className={`tut-chip${chip.tone ? ' tut-chip--' + chip.tone : ''}`}>{chip.t}</span>)}</div>;
    case 'media':
      return <Media key={key} kind={block.kind} tag={block.tag} duration={block.duration} label={block.label} caption={block.caption} src={block.src} stepTitle={stepTitle} />;
    case 'callout':
      return <Callout key={key} kind={block.kind} title={block.title} c={block.c} />;
    default:
      return null;
  }
}

const HelpTutorialPage: React.FC = () => {
  const navigate = useNavigate();
  const [article, setArticle] = useState<ArticleItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState('step-1');
  const stageGroupsRef = useRef<StageGroup[]>([]);

  useEffect(() => {
    apiPublicGet('/articles/tutorial')
      .then(r => r.body.json() as unknown as Promise<ArticleItem>)
      .then(data => {
        setArticle(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const stageGroups = article ? groupBlocks(article.blocks) : [];
  stageGroupsRef.current = stageGroups;

  useEffect(() => {
    if (!article) return;
    const ids = stageGroupsRef.current.flatMap(s => s.steps.map(st => st.stepId));
    if (ids.length === 0) return;
    const obs = new IntersectionObserver((entries) => {
      const vis = entries.filter(e => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (vis[0]) setActive((vis[0].target as HTMLElement).id);
    }, { rootMargin: '-130px 0px -60% 0px', threshold: 0 });
    ids.forEach(id => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [article]);

  const scrollTo = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 130, behavior: 'smooth' });
  };

  const firstStepId = stageGroups[0]?.steps[0]?.stepId ?? 'step-1';

  return (
    <>
    <RouteSeo path="/help/tutorial" />
    <div className="tutorial-page">

      <div className="help-crumbs">
        <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Chaptery</a><span className="sep">/</span>
        <a href="/help" onClick={e => { e.preventDefault(); navigate('/help'); }}>Help Center</a><span className="sep">/</span><span>Tutorial</span>
      </div>

      <section className="tut-hero">
        <div className="tut-hero-inner">
          <span className="eyebrow">Getting Started</span>
          <h1>Write your book — with a <em>whole world</em> behind it</h1>
          <p className="tut-hero-sub">
            Chaptery is an authoring tool, first and last. This walkthrough takes you from a blank
            page to a finished manuscript — and shows you how Chaptery's world-building keeps every
            character, place, and connection at your fingertips while you write. When you're done,
            you'll export a clean, print-ready PDF.
          </p>
          <div className="tut-hero-meta">
            <span className="tut-meta-item"><Clock size={15} /> ~20 minutes</span>
            <span className="tut-meta-item"><ListChecks size={15} /> 5 stages</span>
            <span className="tut-meta-item"><PenLine size={15} /> No writing experience needed</span>
          </div>
          <div className="tut-hero-actions">
            <button className="btn-primary" onClick={scrollTo(firstStepId)} style={{ fontSize: '1.05rem', padding: '14px 28px' }}>Start the walkthrough</button>
            <button className="btn-secondary" style={{ fontSize: '1.05rem', padding: '14px 28px' }} onClick={() => navigate('/downloads')}>Download Chaptery</button>
          </div>
        </div>
      </section>

      <section className="tut-overview">
        <Media kind="video" tag="Full walkthrough" duration="13:53" src="https://youtu.be/Vha6r5tvL1Q"
          caption="**Prefer to watch?** The full walkthrough covers every step below, start to finish." />
      </section>

      <div className="tut-body">

        <nav className="tut-toc">
          <p className="tut-toc-title">In this tutorial</p>
          {loading ? (
            <div style={{ padding: '8px 14px', fontSize: 12, color: '#aaa' }}>Loading…</div>
          ) : (
            stageGroups.map(stage => (
              <div className="tut-toc-stage" key={stage.stageN}>
                <p className="tut-toc-stage-label">
                  <span className="n">{String(stage.stageN).padStart(2, '0')}</span> {stage.t}
                </p>
                <ul className="tut-toc-links">
                  {stage.steps.map(st => (
                    <li key={st.stepId}>
                      <a href={`#${st.stepId}`} onClick={scrollTo(st.stepId)}
                         className={`tut-toc-link ${active === st.stepId ? 'active' : ''}`}>{st.title}</a>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </nav>

        <div className="tut-content">
          {loading ? (
            <div style={{ padding: 40, color: '#aaa' }}>Loading tutorial…</div>
          ) : (
            stageGroups.map(stage => (
              <section className="tut-stage" key={stage.stageN}>
                <div className="tut-stage-head">
                  <span className="stage-n">{String(stage.stageN).padStart(2, '0')}</span>
                  <h2>{stage.t}</h2>
                </div>
                {stage.steps.map(step => (
                  <div className="tut-step" id={step.stepId} data-step={step.stepId} key={step.stepN}>
                    <div className="tut-step-head">
                      <span className="tut-step-n">{step.stepN}</span>
                      <h3>{step.title}</h3>
                    </div>
                    <div className="tut-step-body">
                      {step.blocks.map((block, bi) => renderContentBlock(block, bi, step.title))}
                    </div>
                  </div>
                ))}
              </section>
            ))
          )}
        </div>
      </div>

      <section className="tut-finish">
        <div className="tut-finish-inner">
          <h2>That's a book.</h2>
          <p>You've set up Chaptery, written and organized a manuscript, built a living world around it, and exported a print-ready PDF. Here's where to go deeper.</p>
          <div className="tut-next-grid">
            <a className="tut-next-card" href="/help/article/world-building" onClick={e => { e.preventDefault(); navigate('/help/article/world-building'); }}>
              <span className="tut-next-icon"><Share2 size={20} /></span>
              <h4>Relationship mapping</h4>
              <p>Get the most out of the relationship map — typed links, focus mode, and auto-layout.</p>
              <span className="tut-next-link">Read the guide →</span>
            </a>
            <div className="tut-next-card">
              <span className="tut-next-icon"><Globe2 size={20} /></span>
              <h4>World vocabulary</h4>
              <p>Design custom roles, archetypes, and relationship types for your world.</p>
              <span className="tut-next-link" style={{ color: 'var(--color-text-disabled)' }}>Coming soon</span>
            </div>
            <div className="tut-next-card">
              <span className="tut-next-icon"><Layers size={20} /></span>
              <h4>Series &amp; reuse</h4>
              <p>Share characters, locations, and events across every book in your series.</p>
              <span className="tut-next-link" style={{ color: 'var(--color-text-disabled)' }}>Coming soon</span>
            </div>
          </div>
        </div>
      </section>

    </div>
    </>
  );
};

export default HelpTutorialPage;
